// src/services/authService.js
import axios from 'axios'
import api from './api'

const API_URL = process.env.VUE_APP_BACKEND_API_URL

class AuthService {
  businessRole = null

  async login(credentials) {
    const { data } = await axios.post(`${API_URL}/users/login/`, credentials, {
      headers: { 'Content-Type': 'application/json' },
    })

    if (data.token) {
      localStorage.setItem('token', data.token)
      const profileResp = await axios.get(`${API_URL}/users/me/`, {
        headers: { Authorization: `Token ${data.token}` },
      })
      const userRecord = {
        token: data.token,
        ...profileResp.data,
      }
      localStorage.setItem('user', JSON.stringify(userRecord))

      // This part is async and must complete before we return
      await this.fetchPrimaryBusinessRole()

      // Return the userRecord AFTER everything is done
      return userRecord
    }

    return null // Return null if login fails
  }

  // --- Google Login Flow ---
  async googleLogin(payload) {
    // Send the Google JWT and optional referral code to the new Django endpoint
    const { data } = await axios.post(
      `${API_URL}/users/google-auth/`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    )

    if (data.token) {
      // Mirror the exact same profile-fetching and caching flow as the standard login
      localStorage.setItem('token', data.token)
      const profileResp = await axios.get(`${API_URL}/users/me/`, {
        headers: { Authorization: `Token ${data.token}` },
      })

      const userRecord = {
        token: data.token,
        ...profileResp.data,
      }
      localStorage.setItem('user', JSON.stringify(userRecord))

      // Await role fetching so Vuex has all the data it needs to route the user
      await this.fetchPrimaryBusinessRole()

      return userRecord
    }

    return null
  }

  logout() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    this.businessRole = null
  }

  async register(payload) {
    const res = await axios.post(`${API_URL}/users/`, payload, {
      headers: { 'Content-Type': 'application/json' },
    })
    return res.data
  }

  getCurrentUser() {
    try {
      return JSON.parse(localStorage.getItem('user'))
    } catch {
      return null
    }
  }

  getToken() {
    return localStorage.getItem('token')
  }

  /**
   * Fetches the user's first-found business role and caches it.
   * This is now the single source of truth for fetching the role.
   */
  async fetchPrimaryBusinessRole() {
    if (this.businessRole) {
      return this.businessRole
    }
    const user = this.getCurrentUser()
    if (!user?.public_id) {
      return null
    }

    try {
      const { data: roles } = await api.get('/users/user-business-roles/', {
        params: { user_public_id: user.public_id },
      })

      this.businessRole = roles[0] || null

      return this.businessRole
    } catch (error) {
      this.businessRole = null
      return null
    }
  }

  /**
   * Simple getter, no longer fetches data.
   */
  getBusinessId() {
    return this.businessRole?.business || null
  }

  /**
   * Simple getter, no longer fetches data.
   */
  getUserRole() {
    return this.businessRole?.role || null
  }

  getUserId() {
    return this.getCurrentUser()?.public_id || null
  }

  /**
   * Sends a verification token to the backend to activate a user account.
   * @param {string} token - The UUID token from the verification email link.
   * @returns {Promise<object>} The response data from the API.
   */
  async verifyEmail(token) {
    const { data } = await api.get(`/users/verify-email/${token}/`)
    return data
  }

  /**
   * Requests a password reset link for a given email address.
   * @param {string} email - The user's email address.
   * @returns {Promise<object>} The response data from the API.
   */
  async requestPasswordReset(email) {
    const { data } = await api.post('/users/password-reset/', {
      email,
    })
    return data
  }

  /**
   * Submits a password reset token and a new password to the backend.
   * @param {object} payload - The data for resetting the password.
   * @param {string} payload.token - The password reset token.
   * @param {string} payload.new_password - The new password.
   * @returns {Promise<object>} The response data from the API.
   */
  async resetPasswordConfirm(payload) {
    const { data } = await api.post('/users/password-reset/confirm/', payload)
    return data
  }

  /**
   * Requests a new email verification link.
   * @param {string} email - The user's email address.
   * @returns {Promise<object>} The response data from the API.
   */
  async resendVerificationEmail(email) {
    const { data } = await api.post('/users/resend-verification/', { email })
    return data
  }
}

export default new AuthService()
